import api from "./axios";

export type EntityType = "truck" | "city" | "vendor" | "user" | "truckGroup" | "truckHistory";

export interface ImportDataPayload {
  entity: EntityType;
  data: Record<string, any>[];
  execute?: boolean;
}

export interface ValidationResult {
  entity: Record<string, any>;
  validation: {
    isValid: boolean;
    invalidReason?: string;
  };
}

export interface ImportDataResponse {
  entity: string;
  total: number;
  valid: number;
  invalid: number;
  imported?: number;
  entities?: Record<string, any>[];
  validations?: ValidationResult[];
}

export const importDataApi = async (payload: ImportDataPayload): Promise<ImportDataResponse> => {
  try {
    const response = await api.post<ImportDataResponse | { data: ImportDataResponse }>(
      "/api/v1/dtools/dataImporter",
      payload,
    );
    const data = response.data as ImportDataResponse | { data: ImportDataResponse };
    if ("data" in data && typeof data.data === "object" && "entity" in data.data) {
      return data.data;
    }
    return data as ImportDataResponse;
  } catch (error) {
    console.error("Error importing data:", error);
    throw error;
  }
};

export type LogislyRef = {
  entityType?: string;
  externalId?: string;
  label?: string;
} | null;

export type ReferenceUpdatePlan = {
  collection: string;
  field: string;
  matchedCount: number;
};

export type DedupeUser = {
  _id: string;
  name: string;
  role: string;
  timezone: string | null;
  deactivated: boolean;
  isTempPassword: boolean;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
};

export type DedupeCity = {
  _id: string;
  name: string;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
};

export type DedupeTruck = {
  _id: string;
  licensePlate: string;
  type: string;
  retired: boolean;
  suggestedDriverId: string | null;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
  currentState: {
    status: string;
    locationCityId: string | null;
    destinationCityId: string | null;
    timestamp: string | null;
  };
};

export type UserMergeFields = {
  name: string;
  role: string;
  timezone: string | null;
  deactivated: boolean;
  isTempPassword: boolean;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
};

export type CityMergeFields = {
  name: string;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
};

export type TruckMergeFields = {
  licensePlate: string;
  type: string;
  retired: boolean;
  suggestedDriverId: string | null;
  initiallyCreatedFrom: "MANUAL" | "LOGISLY";
  initialLogislyRef: LogislyRef;
};

export type UserMergePlan = {
  keepUser: DedupeUser;
  discardUser: DedupeUser;
  resultingUser: DedupeUser;
  referenceUpdates: ReferenceUpdatePlan[];
  refreshTokensToDelete: number;
  willDeleteUserId: string;
};

export type CityMergePlan = {
  keepCity: DedupeCity;
  discardCity: DedupeCity;
  resultingCity: DedupeCity;
  referenceUpdates: ReferenceUpdatePlan[];
  willDeleteCityId: string;
};

export type TruckMergePlan = {
  keepTruck: DedupeTruck;
  discardTruck: DedupeTruck;
  resultingTruck: DedupeTruck;
  referenceUpdates: ReferenceUpdatePlan[];
  willDeleteTruckId: string;
};

type DryRunResponse<TPlan> = {
  dryRun: true;
  dryRunToken: string;
  plan: TPlan;
};

type ApplyResponse<TPlan> = {
  dryRun: false;
  applied: true;
  plan: TPlan;
};

const unwrapData = <T>(payload: T | { data: T }): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

export const fetchUserDedupeCandidatesApi = async () => {
  const response = await api.get("/api/v1/dtools/dedupe/users/candidates");
  return unwrapData(response.data) as {
    groups: Array<{ score: number; users: DedupeUser[] }>;
    allUsers: DedupeUser[];
    threshold: number;
    totalUsers: number;
  };
};

export const dryRunUserMergeApi = async (payload: {
  keepUserId: string;
  discardUserId: string;
  fields: UserMergeFields;
}): Promise<DryRunResponse<UserMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/users/dry-run", payload);
  return unwrapData(response.data);
};

export const applyUserMergeApi = async (payload: {
  keepUserId: string;
  discardUserId: string;
  fields: UserMergeFields;
  dryRunToken: string;
}): Promise<ApplyResponse<UserMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/users/apply", payload);
  return unwrapData(response.data);
};

export const fetchCityDedupeCandidatesApi = async () => {
  const response = await api.get("/api/v1/dtools/dedupe/cities/candidates");
  return unwrapData(response.data) as {
    groups: Array<{ score: number; cities: DedupeCity[] }>;
    allCities: DedupeCity[];
    threshold: number;
    totalCities: number;
  };
};

export const dryRunCityMergeApi = async (payload: {
  keepCityId: string;
  discardCityId: string;
  fields: CityMergeFields;
}): Promise<DryRunResponse<CityMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/cities/dry-run", payload);
  return unwrapData(response.data);
};

export const applyCityMergeApi = async (payload: {
  keepCityId: string;
  discardCityId: string;
  fields: CityMergeFields;
  dryRunToken: string;
}): Promise<ApplyResponse<CityMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/cities/apply", payload);
  return unwrapData(response.data);
};

export const fetchTruckDedupeCandidatesApi = async () => {
  const response = await api.get("/api/v1/dtools/dedupe/trucks/candidates");
  return unwrapData(response.data) as {
    groups: Array<{ score: number; trucks: DedupeTruck[] }>;
    allTrucks: DedupeTruck[];
    threshold: number;
    totalTrucks: number;
  };
};

export const dryRunTruckMergeApi = async (payload: {
  keepTruckId: string;
  discardTruckId: string;
  fields: TruckMergeFields;
}): Promise<DryRunResponse<TruckMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/trucks/dry-run", payload);
  return unwrapData(response.data);
};

export const applyTruckMergeApi = async (payload: {
  keepTruckId: string;
  discardTruckId: string;
  fields: TruckMergeFields;
  dryRunToken: string;
}): Promise<ApplyResponse<TruckMergePlan>> => {
  const response = await api.post("/api/v1/dtools/dedupe/trucks/apply", payload);
  return unwrapData(response.data);
};
